export const REQUIRED_COLUMNS = ['shift_id', 'driver_id'] as const;

// Reason codes -> mapped to i18n strings in the component.
export enum AssignmentIssueReason {
  MissingColumns = 'missing_columns',
  MissingFields = 'missing_fields',
  NotANumber = 'not_a_number',
  Duplicate = 'duplicate',
  InvalidDriver = 'invalid_driver',
  AlreadyAssigned = 'already_assigned',
  NotInView = 'not_in_view',
}

export interface AssignmentRow {
  line: number; // 1-based line number in the source file (for error messages)
  shiftId: number;
  driverId: number;
}

export interface AssignmentIssue {
  line: number;
  shiftId?: string;
  driverId?: string;
  reason: AssignmentIssueReason;
}

export interface ParseResult {
  rows: AssignmentRow[]; // well-formed rows (still subject to validateAssignments)
  issues: AssignmentIssue[]; // per-row format errors
  fatal?: AssignmentIssueReason; // whole-file error (empty file / missing columns)
}

export interface ValidationContext {
  // Authoritative set of valid driver (person) ids for the location's loaded
  // people. Enforced whenever provided — an empty set fails every row. Pass
  // `undefined` only to intentionally skip the driver-membership check.
  driverIds?: Set<number>;
  // Shift ids we have actually loaded (the current scheduler window). Assignment
  // status is only trustworthy for these; a shift_id absent from this set is
  // flagged NotInView and never submitted. Omit to skip the in-view check.
  loadedShiftIds?: Set<number>;
  // Subset of loaded shifts that already have a driver (assignedPersonId != null).
  assignedShiftIds?: Set<number>;
}

export interface ValidationResult {
  toAssign: AssignmentRow[];
  issues: AssignmentIssue[];
}

const stripBom = (value: string): string => value.replace(/^\uFEFF/, '');

const splitCsvLine = (line: string): string[] => line.split(',').map((cell) => cell.trim());

/**
 * Parse raw CSV text into well-formed rows + per-row format issues.
 * Tolerates BOM, CRLF/LF, blank lines, surrounding whitespace, extra columns,
 * and arbitrary column order (columns are matched by header name).
 */
export const parseAssignmentCsv = (text: string): ParseResult => {
  const rows: AssignmentRow[] = [];
  const issues: AssignmentIssue[] = [];

  const lines = stripBom(text ?? '').split(/\r?\n/);

  // Keep original 1-based line numbers while dropping blank lines.
  const nonEmpty = lines
    .map((content, index) => ({ content, line: index + 1 }))
    .filter((entry) => entry.content.trim().length > 0);

  if (nonEmpty.length === 0) {
    return { rows, issues, fatal: AssignmentIssueReason.MissingColumns };
  }

  const header = splitCsvLine(nonEmpty[0].content).map((cell) => cell.toLowerCase());
  const shiftIdx = header.indexOf('shift_id');
  const driverIdx = header.indexOf('driver_id');

  if (shiftIdx === -1 || driverIdx === -1) {
    return { rows, issues, fatal: AssignmentIssueReason.MissingColumns };
  }

  for (let i = 1; i < nonEmpty.length; i += 1) {
    const { content, line } = nonEmpty[i];
    const cells = splitCsvLine(content);
    const rawShift = cells[shiftIdx] ?? '';
    const rawDriver = cells[driverIdx] ?? '';

    if (!rawShift || !rawDriver) {
      issues.push({
        line,
        shiftId: rawShift,
        driverId: rawDriver,
        reason: AssignmentIssueReason.MissingFields,
      });
      continue;
    }

    const shiftId = Number(rawShift);
    const driverId = Number(rawDriver);
    if (!Number.isInteger(shiftId) || !Number.isInteger(driverId)) {
      issues.push({
        line,
        shiftId: rawShift,
        driverId: rawDriver,
        reason: AssignmentIssueReason.NotANumber,
      });
      continue;
    }

    rows.push({ line, shiftId, driverId });
  }

  return { rows, issues };
};

/**
 * Apply semantic validation to well-formed rows (fail-closed):
 *   - duplicate shift_id within the file (first occurrence wins)
 *   - driver_id must belong to the location's people when `driverIds` is provided
 *     (an empty set fails every row; only `undefined` skips the check)
 *   - when `loadedShiftIds` is provided, a shift_id outside that set is flagged
 *     NotInView (we cannot verify it is unassigned, so we never submit it)
 *   - a loaded shift that already has a driver is skipped (AlreadyAssigned)
 */
export const validateAssignments = (
  rows: AssignmentRow[],
  context: ValidationContext = {},
): ValidationResult => {
  const { driverIds, loadedShiftIds, assignedShiftIds } = context;
  const toAssign: AssignmentRow[] = [];
  const issues: AssignmentIssue[] = [];
  const seenShiftIds = new Set<number>();

  rows.forEach((row) => {
    const asIssue = (reason: AssignmentIssueReason) =>
      issues.push({
        line: row.line,
        shiftId: String(row.shiftId),
        driverId: String(row.driverId),
        reason,
      });

    if (seenShiftIds.has(row.shiftId)) {
      asIssue(AssignmentIssueReason.Duplicate);
      return;
    }
    seenShiftIds.add(row.shiftId);

    // Driver must belong to the location. Enforced whenever a set is supplied —
    // an empty set means "no valid drivers" (fail-closed), never skipped.
    if (driverIds && !driverIds.has(row.driverId)) {
      asIssue(AssignmentIssueReason.InvalidDriver);
      return;
    }

    // Assignment status is only trustworthy for shifts we have loaded. A shift
    // we cannot see is skipped rather than blindly overwritten.
    if (loadedShiftIds && !loadedShiftIds.has(row.shiftId)) {
      asIssue(AssignmentIssueReason.NotInView);
      return;
    }

    if (assignedShiftIds && assignedShiftIds.has(row.shiftId)) {
      asIssue(AssignmentIssueReason.AlreadyAssigned);
      return;
    }

    toAssign.push(row);
  });

  return { toAssign, issues };
};
